import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, delay, map, Observable, of, startWith, Subscriber, Subscription, switchMap, throwError } from 'rxjs';
import { combineLatestAll, concatMap, expand, filter, finalize, mergeMap, repeat, take, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs/operators';
import { AppContext } from 'src/app/app/context';
import { UserDatasetDto, UserDatasetRefreshStatus, UserDatasetService, UserDatasetState, VisualService } from 'src/app/services/api.services';
import { ContentLoadState } from 'src/app/shared/components/content-placeholder/content-placeholder.component';
import { AppError, catchAppError } from 'src/app/shared/utils';
import { DataModel } from '../dataModel';
import { DataModelResolver } from '../dataModel/DataModelResolver';
import { logger } from '../utils/logger';
import { getRepeatDelay } from '../visuals/data/impl/DataQuery/utils';

const LOGGER = logger('UserDatasetLoader');

@Injectable({ providedIn: 'root' })
export class UserDatasetLoader {
  readonly appContext = inject(AppContext);
  readonly dataModelResolver = inject(DataModelResolver);
  readonly userDatasetService = inject(UserDatasetService);
  readonly visualService = inject(VisualService);

  /**
   * Loads details about a user dataset.
   * Will start and track refresh process if necessary.
   * Handles additonal checks like dataset being deleted, etc.
   **/
  load(
    userDatasetId: number,
    options?: { includeContent?: boolean; throwOnFail?: boolean; forcedRefresh?: boolean, tryLoadModelOnFail?: boolean }): Observable<UserDatasetLoadResult> {
    let currentState: UserDatasetLoadResult = {
      organisationId: this.appContext.organisationId(),
      userDatasetId,
      includeContent: !!options?.includeContent,
      forcedRefresh: !!options?.forcedRefresh,
      throwOnFail: !!options?.throwOnFail,
      wasRefreshed: false,
      tryLoadModelOnFail: !!options?.tryLoadModelOnFail,

      state: !!options?.forcedRefresh ? ContentLoadState.Refreshing : ContentLoadState.Loading,
    };

    return new Observable((subscriber) => {
      let update = of(currentState).pipe(
        switchMap(s => {
          if (s.forcedRefresh)
            return this.userDatasetService.refreshUserDataset(s.userDatasetId).pipe(
              map((jobId) => ({ ...s, jobId: jobId?.id }))
            );

          return of(s);
        }),
        tap(newState => currentState = newState), //Save state in case of error (need for jobId)
        switchMap(s => this.initialDatasetLoad(s, subscriber)),
        switchMap(state => {
          // If dataset has data (not expired/purged) and latest refresh is also successful, then load process is complete
          if (this.isDataReady(state))
            return of({ ...state, state: ContentLoadState.Loaded });

          if (state.userDataset.state == UserDatasetState.Deleted)
            return of({ ...state, state: ContentLoadState.Deleted });

          //Trigger refresh and check untill complete
          return this.userDatasetService.ensureDataAvailable(state.userDatasetId).pipe(
            map((jobId) => ({ ...state, jobId: jobId?.id ?? state.jobId })),
            tap(newState => currentState = newState), //Save state in case of error (need for jobId)
            switchMap((s) => this.checkRefreshUntilComplete(s, subscriber)),
          );
        }),
        switchMap(state => {
          if (state.state == ContentLoadState.Deleted)
            return of({ ...state, done: true });

          let result: Observable<UserDatasetLoadResult> = null;

          if (state.userDataset.latestRefreshResult == UserDatasetRefreshStatus.Succeeded || state.tryLoadModelOnFail) {
            result = this.loadModel(state);
          }
          else {
            result = of({
              ...state,
              dataModel: new DataModel(),
            });
          }

          return result.pipe(
            map((r) => ({
              ...r,
              done: true,
              success: r.userDataset.latestRefreshResult == UserDatasetRefreshStatus.Succeeded,
              cancelled: state.userDataset.latestRefreshResult == UserDatasetRefreshStatus.Cancelled,
            })));
        }),
        catchAppError(err => {
          if (currentState.throwOnFail)
            return throwError(() => err);

          currentState = {
            ...currentState,
            done: true,
            state: ContentLoadState.Error,
            error: err,
            success: false,
          };

          return of(currentState);
        }, { showErrorSnack: false }),
        take(1),
        tap(finalState => {
          subscriber.next(finalState);
        }),
        finalize(() => {
          subscriber.unsubscribe();
        })
      ).subscribe();

      return () => {
        update.unsubscribe();
      };
    });
  }

  private initialDatasetLoad(state: UserDatasetLoadResult, stateSub: Subscriber<UserDatasetLoadResult>): Observable<UserDatasetLoadResult> {
    return this.userDatasetService.getUserDataset(state.userDatasetId, state.includeContent, true).pipe(
      map(userDataset => ({
          ...state, userDataset
        })),
      tap(state => {
        stateSub.next(state);
      }),
    );
  };

  private isDataReady(state: UserDatasetLoadResult): boolean {
    return state.userDataset.state == UserDatasetState.HasData
      && state.userDataset.latestRefreshResult == UserDatasetRefreshStatus.Succeeded;
  };

  private checkRefreshUntilComplete(state: UserDatasetLoadResult, stateSub: Subscriber<UserDatasetLoadResult>): Observable<UserDatasetLoadResult> {
    let retry = 0;
    return this.userDatasetService.getUserDataset(state.userDatasetId, false, true).pipe(
      delay(getRepeatDelay(retry, { initial: 1500, max: 5000, jitter: 400 })),
      map(u => (<UserDatasetLoadResult>{
        ...state,
        userDataset: {...u, content: state.userDataset.content }, //take the newest value but perpetuate content since this call never returns content
        state: ContentLoadState.Refreshing
      })),
      tap(state => {
        retry += 1;
        stateSub.next(state);
      }),
      repeat(),
      filter(state => ![UserDatasetRefreshStatus.Pending, UserDatasetRefreshStatus.Running].includes(state.userDataset.latestRefreshResult)),
      switchMap(state => {
        //After the dataset has finished loading and we want to include the content try to load the content one last time
        if(!state.includeContent)
          return of(state);

        return this.userDatasetService.getUserDataset(state.userDatasetId, true, false).pipe(
          map(u => ({
            ...state,
            userDataset: { ...u, latestRefreshLog: state.userDataset.latestRefreshLog  } //take the newest value but perpetuate the refreshlog since this call never returns the refreshlog
          }))
        )
      }),
      map(state => {
        if (this.isDataReady(state)) {
          return {
            ...state,
            state: ContentLoadState.Loaded,
            wasRefreshed: true,
          };
        }

        return {
          ...state,
          state: ContentLoadState.Error,
          error: state.userDataset.latestRefreshLog.error,
          wasRefreshed: true,
        }
      }),
      take(1)
    );
  }
  
  private loadModel(r: UserDatasetLoadResult) {
    return this.dataModelResolver.getUserDatasetModel(r.userDatasetId, { organisationId: r.organisationId, skipNoValidationWindow: true, reload: r.wasRefreshed }).pipe(
      map(dataModel => ({
        ...r,
        dataModel,
      }))
    );
  }
  // Attempt to get current dataset and data model while ignoring if they are not refreshed yet
  tryPreloadModelAndDto(userDatasetId: number, organisationId: number, includeContent: boolean): Observable<PreLoadModelAndDtoResult> {
    return this.userDatasetService.getUserDataset(userDatasetId, includeContent, true).pipe(
      switchMap((ud) => {
        return this.dataModelResolver.getUserDatasetModel(userDatasetId, { organisationId }).pipe(
          map((d) => <PreLoadModelAndDtoResult>{userdataset: ud, dataModel: d})
        );
      }),
    );
  }
}

export interface UserDatasetLoadResult {
  readonly organisationId: number;
  readonly userDatasetId: number;
  readonly includeContent: boolean;
  readonly forcedRefresh: boolean;
  readonly throwOnFail: boolean;
  readonly wasRefreshed: boolean;
  readonly tryLoadModelOnFail: boolean;

  readonly jobId?: number;

  readonly state?: ContentLoadState;
  readonly cancelled?: boolean;
  readonly error?: any;
  readonly success?: boolean;
  readonly done?: boolean;

  readonly userDataset?: UserDatasetDto;
  readonly dataModel?: DataModel;
}

export interface PreLoadModelAndDtoResult {
  userdataset: UserDatasetDto,
  dataModel: DataModel
}
